import api from "./api";

function throwFromAxios(error: any, fallback: string): never {
  const rawErr = error?.response?.data?.error;
  const msg =
    (typeof rawErr === "string" && rawErr.trim() !== "" ? rawErr.trim() : null) ||
    (typeof error?.message === "string" && error.message.trim() !== ""
      ? error.message
      : null) ||
    fallback;
  const err = new Error(msg) as Error & { code?: string; httpStatus?: number };
  const code = error?.response?.data?.code;
  if (typeof code === "string" && code.trim()) err.code = code.trim();
  if (typeof error?.response?.status === "number") {
    err.httpStatus = error.response.status;
  }
  throw err;
}
import {
  IPedidoListGerenciarResponse,
  IPedidoDetalheResponse,
  IEnvioUpdate,
  StatusPedido,
  EmitirFaturamentoPedidoBody,
  EmitirFaturamentoPedidoResponse,
  SeparacaoPedidoActionResponse,
  ExpedicaoPedidoActionResponse,
  GerarEtiquetaPedidoBody,
  RetiradaPedidoActionResponse,
  ConfirmarRetiradaPedidoBody,
} from "interfaces/loja";

/**
 * API: Listar pedidos da loja (gerenciar) - GET /loja/pedidos
 * Backend retorna { pedidos, total, currentPage, totalPages }
 */
export async function listarPedidosLoja(params?: {
  page?: number;
  limit?: number;
  status?: string;
  query?: string;
  dataInicio?: string;
  dataFim?: string;
}) {
  try {
    const search = new URLSearchParams();
    if (params?.page != null) search.set("page", String(params.page));
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.status) search.set("status", String(params.status));
    if (params?.query) search.set("query", String(params.query));
    if (params?.dataInicio) search.set("dataInicio", String(params.dataInicio));
    if (params?.dataFim) search.set("dataFim", String(params.dataFim));

    const { data, status } = await api.get<IPedidoListGerenciarResponse>(
      `/loja/pedidos${search.toString() ? `?${search}` : ""}`
    );
    return { data, status } as {
      data: IPedidoListGerenciarResponse;
      status: number;
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

/**
 * API: Detalhar pedido da loja - GET /loja/pedidos/:pedidoId
 */
export async function detalharPedidoLoja(pedidoId: string) {
  try {
    const { data, status } = await api.get<IPedidoDetalheResponse>(
      `/loja/pedidos/${pedidoId}`
    );
    return { data, status } as { data: IPedidoDetalheResponse; status: number };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

/**
 * API: Atualizar status do pedido - PUT /loja/pedidos/status/:id
 */
export async function atualizarStatusPedidoLoja(
  pedidoId: string,
  statusPedido: StatusPedido
) {
  try {
    const { data, status } = await api.put(`/loja/pedidos/status/${pedidoId}`, {
      status: statusPedido,
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

/**
 * API: Criar envio do pedido - POST /loja/pedidos/envios/:pedidoId
 * Body: { tipo (obrig.), provedor?, servico?, rastreio?, custo?, prazo?, melhorEnvioOrderId? }
 */
export async function criarEnvioPedidoLoja(
  pedidoId: string,
  payload?: {
    tipo?: "CORREIOS" | "RETIRADA_LOJA" | "RETIRADA_EVENTO" | "DIGITAL" | "TRANSPORTADORA";
    provedor?: string;
    servico?: string;
    rastreio?: string;
    custo?: number;
    prazo?: string;
    melhorEnvioOrderId?: string | null;
  }
) {
  try {
    const body = { tipo: "TRANSPORTADORA" as const, ...(payload ?? {}) };
    const { data, status } = await api.post(
      `/loja/pedidos/envios/${pedidoId}`,
      body
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

/**
 * API: Atualizar envio do pedido - PUT /loja/pedidos/envios/:envioId
 */
export async function atualizarEnvioPedidoLoja(
  envioId: string,
  payload?: IEnvioUpdate
) {
  try {
    const { data, status } = await api.put(
      `/loja/pedidos/envios/${envioId}`,
      payload ?? {}
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

/**
 * API: Criar evento de envio - POST /loja/pedidos/eventos/envios/:envioId
 * Body: { status?, descricao?, dataEvento } (dataEvento em ISO 8601)
 */
export async function criarEventoEnvioPedidoLoja(
  envioId: string,
  payload?: { status?: string; descricao?: string; dataEvento?: string }
) {
  try {
    const { data, status } = await api.post(
      `/loja/pedidos/eventos/envios/${envioId}`,
      payload ?? {}
    );
    return { data, status };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.error || error.message || "Falha na comunicação"
    );
  }
}

type EmitirFatPayload = Omit<EmitirFaturamentoPedidoBody, "pedidoId">;

/**
 * API: Emitir/atualizar faturamento — POST /loja/pedidos/:pedidoId/faturamento/emitir
 */
export async function emitirFaturamentoPedidoLoja(
  pedidoId: string,
  payload: EmitirFatPayload
) {
  const body: EmitirFaturamentoPedidoBody = {
    pedidoId,
    numeroNota: payload.numeroNota.trim(),
    serieNota: payload.serieNota?.trim() || undefined,
    chaveNfe: payload.chaveNfe?.trim() || undefined,
    valorNota:
      payload.valorNota === undefined || payload.valorNota === null
        ? undefined
        : Number(payload.valorNota),
    xmlUrl: payload.xmlUrl?.trim() || undefined,
    pdfUrl: payload.pdfUrl?.trim() || undefined,
    observacaoFiscal: payload.observacaoFiscal?.trim() || undefined,
  };

  const { data, status } = await api.post<
    EmitirFaturamentoPedidoResponse | { success?: boolean; message?: string }
  >(`/loja/pedidos/${pedidoId}/faturamento/emitir`, body);

  return { data, status } as {
    data:
      | EmitirFaturamentoPedidoResponse
      | { success?: boolean; message?: string };
    status: number;
  };
}

/**
 * API: Iniciar separação — POST /loja/pedidos/:pedidoId/separacao/iniciar
 */
export async function iniciarSeparacaoPedidoLoja(pedidoId: string) {
  const { data, status } = await api.post<SeparacaoPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/separacao/iniciar`,
    {}
  );
  return { data, status };
}

/**
 * API: Finalizar separação — POST /loja/pedidos/:pedidoId/separacao/finalizar
 */
export async function finalizarSeparacaoPedidoLoja(pedidoId: string) {
  const { data, status } = await api.post<SeparacaoPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/separacao/finalizar`,
    {}
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/expedicao/etiqueta */
export async function gerarEtiquetaPedidoLoja(
  pedidoId: string,
  body?: GerarEtiquetaPedidoBody
) {
  const { data, status } = await api.post<ExpedicaoPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/expedicao/etiqueta`,
    body ?? {}
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/expedicao/postar */
export async function marcarPostadoPedidoLoja(pedidoId: string) {
  const { data, status } = await api.post<ExpedicaoPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/expedicao/postar`,
    {}
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/expedicao/entregar */
export async function marcarEntreguePedidoLoja(pedidoId: string) {
  const { data, status } = await api.post<ExpedicaoPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/expedicao/entregar`,
    {}
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/retirada/pronto */
export async function marcarProntoParaRetiradaPedidoLoja(pedidoId: string) {
  const { data, status } = await api.post<RetiradaPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/retirada/pronto`,
    {}
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/retirada/confirmar */
export async function confirmarRetiradaPedidoLoja(
  pedidoId: string,
  body: ConfirmarRetiradaPedidoBody
) {
  const { data, status } = await api.post<RetiradaPedidoActionResponse>(
    `/loja/pedidos/${pedidoId}/retirada/confirmar`,
    body
  );
  return { data, status };
}

/** POST /loja/pedidos/:id/cancelar — cancelamento operacional (body opcional com motivo). */
export async function cancelarPedidoLojaOperacional(
  pedidoId: string,
  body?: { motivo?: string }
) {
  try {
    const payload =
      body?.motivo != null && String(body.motivo).trim() !== ""
        ? { motivo: String(body.motivo).trim() }
        : {};
    const { data, status } = await api.post(
      `/loja/pedidos/${pedidoId}/cancelar`,
      payload
    );
    return { data, status };
  } catch (error: any) {
    throwFromAxios(error, "Falha ao cancelar pedido.");
  }
}

/** POST /loja/pedidos/:id/solicitar-devolucao */
export async function solicitarDevolucaoPedidoLoja(
  pedidoId: string,
  body?: { motivo?: string }
) {
  try {
    const payload =
      body?.motivo != null && String(body.motivo).trim() !== ""
        ? { motivo: String(body.motivo).trim() }
        : {};
    const { data, status } = await api.post(
      `/loja/pedidos/${pedidoId}/solicitar-devolucao`,
      payload
    );
    return { data, status };
  } catch (error: any) {
    throwFromAxios(error, "Falha ao solicitar devolução.");
  }
}

/** POST /loja/pedidos/:id/confirmar-devolucao */
export async function confirmarDevolucaoPedidoLoja(pedidoId: string) {
  try {
    const { data, status } = await api.post(
      `/loja/pedidos/${pedidoId}/confirmar-devolucao`,
      {}
    );
    return { data, status };
  } catch (error: any) {
    throwFromAxios(error, "Falha ao confirmar devolução.");
  }
}
